import { Settings } from "../../core/services/configService";
import { CrudService } from "../../core/services/crudService";
import { CustomerHelper } from "../../crm/helpers/customerHelper";

export const DayDone = 0;
export const DayUnable = 1;
export const CardGuideline = 3;
export const ScopeProgram = 1;

// The customer's guidelines. With a preview token (admin iframe) only the template is read and nothing is saved.
export class GuidelineApi {
  static base(): string {
    return `${Settings.HostName}/api/publiccustomerguidelines/customer/${CustomerHelper.getStoredCustomer()}`;
  }

  static list(): Promise<any> {
    return new CrudService().httpGet(this.base());
  }

  static detail(id: number | string, previewToken: string): Promise<any> {
    const url = previewToken
      ? `${Settings.HostName}/api/publiccustomerguidelines/preview/${id}?t=${encodeURIComponent(previewToken)}`
      : `${this.base()}/${id}`;
    return new CrudService().httpGet(url);
  }

  static record(id: number, date: string, status: number): Promise<any> {
    return new CrudService().httpPost(`${this.base()}/${id}/days`, { date: date, status: status });
  }

  static undo(id: number, date: string): Promise<any> {
    return new CrudService().httpDelete(`${this.base()}/${id}/days/${date}`);
  }

  // What the patient sends back: every place that asks (the guideline and each session) with its last submission.
  static submissions(id: number | string): Promise<any> {
    return new CrudService().httpGet(`${this.base()}/${id}/submissions`);
  }

  static createVideo(id: number | string, sessionId: number | null): Promise<any> {
    return new CrudService().httpPost(`${this.base()}/${id}/submissions/video`, { sessionId: sessionId });
  }

  static submitVideo(id: number | string, sessionId: number | null, videoId: string): Promise<any> {
    return new CrudService().httpPost(`${this.base()}/${id}/submissions`, { sessionId: sessionId, videoId: videoId });
  }

  static videoStatus(videoId: string): Promise<any> {
    return new CrudService().httpGet(`${this.base()}/videos/${videoId}/status`);
  }

  static submitPhoto(id: number | string, sessionId: number | null, photo: Blob): Promise<any> {
    const body = new FormData();
    if (sessionId) body.append("sessionId", String(sessionId));
    body.append("file", photo, "photo.jpg");
    return new CrudService().httpPost(`${this.base()}/${id}/submissions/photo`, body, undefined, "multipart");
  }

  static photo(submissionId: number): Promise<Blob | null> {
    return new CrudService().httpGetBlob(`${this.base()}/submissions/${submissionId}/photo`);
  }

  // Writing to the professional: the topics that allow it, the patient's messages on one, and sending.
  static messageTopics(): Promise<any> {
    return new CrudService().httpGet(`${this.base()}/messages/topics`);
  }

  static messages(guidelineId: number): Promise<any> {
    return new CrudService().httpGet(`${this.base()}/messages?guidelineId=${guidelineId}`);
  }

  static sendMessage(message: any): Promise<any> {
    return new CrudService().httpPost(`${this.base()}/messages`, message);
  }

  // Civil days travel as yyyy-MM-dd; the API sends them at midnight.
  // Straight to today's session when there is one to do, so the customer does not go through the overview.
  static link(row: any, back: string = ""): string {
    return `/guidelines/${row.id}` + (back ? "?back=" + encodeURIComponent(back) : "");
  }

  static frequency(t: (key: string) => string, n: number): string {
    if (n === 7) return t("every day");
    if (n === 1) return t("once a week");
    return t("{0} days a week").replace("{0}", String(n));
  }

  static day(input: string): string {
    return (input || "").substring(0, 10);
  }

  static addDays(day: string, n: number): string {
    const d = new Date(day + "T12:00:00");
    d.setDate(d.getDate() + n);
    return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
  }

  static mondayOf(day: string): string {
    const back = (new Date(day + "T12:00:00").getDay() + 6) % 7;
    return this.addDays(day, -back);
  }
}
